import { format as formatDate } from "date-fns";
import { v4 as uuidv4 } from "uuid";

import { ISO8601_DATETIME_FORMAT, ISO8601_DATE_FORMAT } from "@constants";
import { formatDateWithTimezone } from "@utils";

import { IObservationPhotoDto } from "../dtos/observation-photo.dto";
import { IObservationUpsertDto } from "../dtos/observation-upsert.dto";
import { IObservationDto } from "../dtos/observation.dto";
import { IViolationDto } from "../dtos/violation.dto";
import { toViolationUpsertDto } from "../utils/to-violation-upsert-dto";

export class Observation {
  readonly id: string;
  appUserId: string;
  appUserFullName: string;
  farmId: string;
  farmName: string;
  farmLandId: string;
  farmLandName: string;
  phenoPhaseId: string | null;
  phenoPhaseName: string;
  cropTypeId: string;
  seasonId: string;
  reportNumber: number;
  comment: string;
  violations: IViolationDto[];
  observationPhotos: IObservationPhotoDto[];
  isReportComplete = false;
  cropId: string;
  cropName: string;
  departmentId: string;
  createdOnClientAt: string | null;
  observationDate = new Date();
  isDeleted: boolean;
  realFarmLandId?: string;
  realFarmLandName?: string;
  updatedAt?: number; // unixtime

  get observationDateHuman(): string {
    return formatDate(this.observationDate, ISO8601_DATE_FORMAT);
  }

  get editDateHuman(): string {
    return this.updatedAt ? formatDate(this.updatedAt, ISO8601_DATETIME_FORMAT) : "-";
  }

  get isOriginal(): boolean {
    return this.realFarmLandId !== undefined
      ? this.realFarmLandId === this.farmLandId
      : true;
  }

  get asDto(): IObservationDto {
    return {
      id: this.id,
      farmId: this.farmId,
      farmLandId: this.farmLandId,
      phenoPhaseId: this.phenoPhaseId,
      cropTypeId: this.cropTypeId,
      seasonId: this.seasonId,
      reportNumber: this.reportNumber,
      comment: this.comment,
      violations: this.violations?.map((violation) => ({ ...violation })),
      appUserFullName: this.appUserFullName,
      cropName: this.cropName,
      farmName: this.farmName,
      farmLandName: this.farmLandName,
      phenoPhaseName: this.phenoPhaseName,
      isReportComplete: this.isReportComplete,
      appUserId: this.appUserId,
      observationDate: formatDate(this.observationDate, ISO8601_DATE_FORMAT),
      updatedAt: this.updatedAt,
      createdOnClientAt: this.createdOnClientAt,
      isDeleted: this.isDeleted,
      photos: this.observationPhotos ?? [],
      cropId: this.cropId,
      departmentId: this.departmentId,
      realFarmLandId: this.realFarmLandId,
      realFarmLandName: this.realFarmLandName,
    };
  }

  get asUpsertDto(): IObservationUpsertDto {
    return {
      id: this.id,
      farmLandId: this.farmLandId,
      seasonId: this.seasonId,
      cropId: this.cropId,
      observationDate: formatDate(this.observationDate, ISO8601_DATE_FORMAT),
      createdOnClientAt: this.createdOnClientAt,
      appUserId: this.appUserId,
      phenoPhaseId: this.phenoPhaseId,
      comment: this.comment,
      violations: (this.violations ?? []).map(toViolationUpsertDto),
      isReportComplete: this.isReportComplete,
    };
  }

  static fromDto(dto: IObservationDto): Observation {
    const model = new Observation(dto.id);
    model.updateFromDto(dto);
    return model;
  }

  updateFromDto(dto: IObservationDto): void {
    this.appUserId = dto.appUserId;
    this.farmId = dto.farmId;
    this.farmLandId = dto.farmLandId;
    this.phenoPhaseId = dto.phenoPhaseId || null;
    this.cropTypeId = dto.cropTypeId;
    this.seasonId = dto.seasonId;
    this.reportNumber = dto.reportNumber;
    this.comment = dto.comment;
    this.violations = dto.violations?.map((violation) => ({ ...violation })) ?? [];
    this.observationPhotos = dto.photos ?? [];
    this.isReportComplete = dto.isReportComplete ?? false;
    this.appUserFullName = dto.appUserFullName ?? "";
    this.cropName = dto.cropName ?? "";
    this.farmName = dto.farmName ?? "";
    this.farmLandName = dto.farmLandName ?? "";
    this.phenoPhaseName = dto.phenoPhaseName ?? "";
    this.createdOnClientAt = dto.createdOnClientAt;
    this.observationDate = new Date(dto.observationDate);
    this.updatedAt = dto.updatedAt;
    this.isDeleted = dto.isDeleted || false;
    this.cropId = dto.cropId;
    this.departmentId = dto.departmentId;
    this.realFarmLandId = dto.realFarmLandId;
    this.realFarmLandName = dto.realFarmLandName;
  }

  constructor(id: string = uuidv4()) {
    this.id = id;
  }

  setFarmId(value: string): void {
    this.farmId = value;
  }

  setPhenoPhaseId(value: string | null): void {
    this.phenoPhaseId = value === "" ? null : value;
  }

  setCropTypeId(value: string): void {
    this.cropTypeId = value;
  }

  setSeasonId(value: string): void {
    this.seasonId = value;
  }

  setComment(value: string): void {
    this.comment = value;
  }

  setViolations(value: IViolationDto[]): void {
    this.violations = value;
  }

  setObservationDate(value: Date): void {
    this.observationDate = value;
  }

  completeReport(): void {
    this.isReportComplete = true;
  }

  invalidateCompleteReport(): void {
    this.isReportComplete = false;
  }

  setAppUserId(appUserId: string): void {
    this.appUserId = appUserId;
  }

  setFarmLandId(farmLandId: string | null): Observation {
    if (farmLandId) {
      this.farmLandId = farmLandId;
    }
    return this;
  }

  resetCreatedOnClientAt(): void {
    const now = new Date();
    const newDate = formatDateWithTimezone(now);
    this.createdOnClientAt = newDate;
  }
}
